import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "../components/SiteLayout";
import { APP_STORE_URL } from "../site/constants";
import { IconArrow } from "../site/icons";
import "./HoeHetWerktPage.css";

const STEPS = [
  {
    id: "paid",
    title: "Bestelling binnen",
    body:
      "Zodra je hebt betaald, komt je order direct binnen in ons systeem. Geen wachtrij, geen onzekerheid.",
    Icon: IconPaid,
  },
  {
    id: "admin",
    title: "Administratie pakt op",
    body:
      "Ons team controleert de bestelling, checkt beschikbaarheid en zet alles klaar voor verdere verwerking.",
    Icon: IconAdmin,
  },
  {
    id: "logistics",
    title: "Logistiek in Suriname",
    body:
      "Lokaal worden de producten verzameld, gecontroleerd en klaargemaakt voor bezorging bij jouw ontvanger.",
    Icon: IconLogistics,
  },
  {
    id: "deliver",
    title: "Bezorging bij je dierbaren",
    body:
      "Onze bezorger brengt het pakket persoonlijk. Wat jij koos, komt precies daar waar het hoort.",
    Icon: IconDeliver,
  },
  {
    id: "capture",
    title: "Moment vastgelegd",
    body:
      "We leggen het moment vast: een foto van de overhandiging, zodat jij de blijdschap van dichtbij voelt.",
    Icon: IconCapture,
  },
  {
    id: "share",
    title: "Alleen voor jou",
    body:
      "De bezorgfoto delen we privé — uitsluitend via de Suriwebwinkel-app en je e-mail. Niet openbaar, niet voor anderen.",
    Icon: IconPrivate,
  },
];

function IconPaid() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M3 10h18" />
      <path d="M7 15h3" />
    </svg>
  );
}

function IconAdmin() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 5h6" />
      <path d="M8 3h8v18H8z" />
      <path d="M11 10h2M11 14h2" />
    </svg>
  );
}

function IconLogistics() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 7h11v10H3z" />
      <path d="M14 10h4l3 3v4h-7z" />
      <circle cx="7" cy="18" r="1.6" />
      <circle cx="17" cy="18" r="1.6" />
    </svg>
  );
}

function IconDeliver() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 10.5L12 4l8 6.5V20a1 1 0 01-1 1h-5v-6H10v6H5a1 1 0 01-1-1z" />
    </svg>
  );
}

function IconCapture() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 8h3l1.5-2h7L17 8h3v11H4z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}

function IconPrivate() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </svg>
  );
}

function IconPhone() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="7" y="3" width="10" height="18" rx="2" />
      <path d="M11 18h2" />
    </svg>
  );
}

function IconMail() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 8l9 6 9-6" />
    </svg>
  );
}

function FlowGraphic() {
  return (
    <svg viewBox="0 0 640 480" fill="none" aria-hidden="true">
      <path
        d="M40 80C120 40 180 160 260 140C340 120 360 40 440 70C520 100 560 200 600 180"
        stroke="rgba(245,211,61,0.7)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M20 220C110 190 150 300 240 280C330 260 370 160 460 190C550 220 580 320 620 300"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="260" cy="140" r="6" fill="#f5d33d" />
      <circle cx="440" cy="70" r="5" fill="rgba(255,255,255,0.8)" />
      <circle cx="240" cy="280" r="5" fill="#f5d33d" />
      <circle cx="460" cy="190" r="6" fill="rgba(255,255,255,0.75)" />
    </svg>
  );
}

export default function HoeHetWerktPage() {
  const trackRef = useRef(null);
  const stepRefs = useRef([]);
  const [visible, setVisible] = useState({});
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const nodes = stepRefs.current.filter(Boolean);
    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisible((prev) => {
          const next = { ...prev };
          entries.forEach((entry) => {
            const id = entry.target.getAttribute("data-step");
            if (id && entry.isIntersecting) next[id] = true;
          });
          return next;
        });
      },
      { threshold: 0.45, rootMargin: "0px 0px -8% 0px" }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const viewH = window.innerHeight || 1;
      const start = viewH * 0.55;
      const end = viewH * 0.35;
      const total = rect.height - (start - end);
      const traveled = start - rect.top;
      const pct = Math.min(100, Math.max(0, (traveled / total) * 100));
      setProgress(pct);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <SiteLayout>
      <div className="hhw">
        <section className="hhw-hero" aria-labelledby="hhw-hero-title">
          <div className="hhw-hero-bg" aria-hidden="true" />
          <div className="hhw-hero-flow" aria-hidden="true">
            <FlowGraphic />
          </div>
          <div className="lp-wrap">
            <div className="hhw-hero-copy">
              <span className="hhw-brand">Suriwebwinkel</span>
              <h1 id="hhw-hero-title">Van betaling tot blij moment</h1>
              <p className="hhw-hero-lead">
                Een helder pad: jij bestelt, wij regelen alles lokaal — en delen
                het moment privé met jou.
              </p>
              <div className="hhw-hero-ctas">
                <a
                  className="lp-btn-primary"
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Start met bestellen
                  <span className="lp-btn-arrow">
                    <IconArrow />
                  </span>
                </a>
                <a className="lp-btn-ghost" href="#proces">
                  Bekijk het proces
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="hhw-journey" id="proces" aria-labelledby="hhw-process-title">
          <div className="lp-wrap">
            <header className="hhw-journey-head">
              <span className="hhw-eyebrow">Het proces</span>
              <h2 id="hhw-process-title">Zes stappen. Geen gedoe.</h2>
              <p>
                Van de betaalde bestelling tot de privé-foto bij je familie —
                zo werkt Suriwebwinkel, van begin tot einde.
              </p>
            </header>

            <div className="hhw-track" ref={trackRef}>
              <div className="hhw-spine" aria-hidden="true">
                <div
                  className="hhw-spine-fill"
                  style={{ height: `${progress}%` }}
                />
              </div>

              <ol className="hhw-steps">
                {STEPS.map((step, index) => {
                  const side = index % 2 === 0 ? "left" : "right";
                  const isVisible = !!visible[step.id];
                  const StepIcon = step.Icon;
                  return (
                    <li
                      key={step.id}
                      data-step={step.id}
                      ref={(el) => {
                        stepRefs.current[index] = el;
                      }}
                      className={`hhw-step hhw-step--${side}${
                        isVisible ? " is-visible is-active" : ""
                      }`}
                    >
                      <div className="hhw-step-node">
                        <StepIcon />
                      </div>
                      <div className="hhw-step-body">
                        <span className="hhw-step-index">
                          Stap {String(index + 1).padStart(2, "0")}
                        </span>
                        <h3>{step.title}</h3>
                        <p>{step.body}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </section>

        <section className="hhw-close" aria-labelledby="hhw-close-title">
          <div className="lp-wrap">
            <div className="hhw-close-panel">
              <div className="hhw-close-inner">
                <p className="hhw-close-badge">
                  <IconLock />
                  Privé gedeeld
                </p>
                <h2 id="hhw-close-title">Het moment is van jullie</h2>
                <p>
                  De foto van de overhandiging gaat nergens heen behalve naar
                  jou — veilig in de app en in je inbox.
                </p>
                <div className="hhw-channels" aria-label="Kanalen">
                  <span className="hhw-channel">
                    <IconPhone />
                    Alleen via de app
                  </span>
                  <span className="hhw-channel">
                    <IconMail />
                    En per e-mail
                  </span>
                </div>
                <div className="hhw-cta-row">
                  <a
                    className="lp-btn-primary"
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download de app
                    <span className="lp-btn-arrow">
                      <IconArrow />
                    </span>
                  </a>
                  <Link className="lp-btn-ghost" to="/producten">
                    Bekijk producten
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="hhw-note">
          <div className="lp-wrap">
            <p>
              Geen dozen inpakken. Geen internationale verzending. Gewoon
              bestellen — wij doen de rest.
            </p>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
